import React, { useMemo, useState, useLayoutEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Button, Card, CardBody, Col, Input, Row } from 'reactstrap';

import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import './PreventiveTaskProjection.css';

import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';

import {
  getPreventiveTaskProjectionDashboard,
  clearPreventiveTaskProjectionDashboard,
} from '../../../../actions/task-schedule/PreventiveTaskProjection';

import { postPreventiveTaskExecution } from '../../../../actions/task-schedule/PreventiveTaskExecution';

import {
  PREVENTIVE_TASK_EXECUTION_STATUS,
  RESOURCE_EXECUTION_MODE,
} from '../../../../config/constants';

import PreventiveTaskProjectionHeader from './PreventiveTaskProjectionHeader';
import PreventiveTaskProjectionMonthGrid from './PreventiveTaskProjectionMonthGrid';
import PreventiveTaskProjectionDayDetail from './PreventiveTaskProjectionDayDetail';

import {
  formatDateTitle,
  getMonthMatrix,
  groupByProjectedDate,
  normalizeDaysToAdd,
  toDateKey,
} from './utils/PreventiveTaskProjectionCalendarUtils';

import PreventiveTaskProjectionSearchForm from './PreventiveTaskProjectionSearchForm';
import { tzNormalizeDate } from '../../../../utils/date';

const DETAIL_MODE = {
  DAY: 'DAY',
  OVERDUE: 'OVERDUE',
  SEARCH: 'SEARCH',
};

const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const normalizeFile = (value) => {
  if (!value) return null;

  if (value instanceof File) return value;

  if (Array.isArray(value)) {
    return normalizeFile(value[0]);
  }

  if (value.length) {
    return value[0];
  }

  if (value.file instanceof File) {
    return value.file;
  }

  if (value.value instanceof File) {
    return value.value;
  }

  return null;
};

const getResourceId = (resource) => {
  if (!resource) return null;

  if (resource.resourceId) return resource.resourceId;

  if (resource.companyResource && resource.companyResource.id) {
    return resource.companyResource.id;
  }

  if (resource.id) return resource.id;

  if (resource.companyResourceId) return resource.companyResourceId;

  if (resource.preventiveTaskResourceId) {
    return resource.preventiveTaskResourceId;
  }

  return null;
};

const getProjectionResourceId = (resource) => {
  if (!resource) return null;

  return (
    resource.projectionResourceId ||
    resource.preventiveTaskProjectionResourceId ||
    null
  );
};

const normalizeSelectedIds = (values) => {
  if (!values) return new Set();

  const arrayValues = Array.isArray(values) ? values : [values];

  return new Set(
    arrayValues
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value)),
  );
};

const normalizeSearchText = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const getProjectionDateKey = (item) => {
  if (!item || !item.projectedDate) return null;

  return toDateKey(item.projectedDate);
};

const isOverdueProjection = (item, todayKey) => {
  const projectedDateKey = getProjectionDateKey(item);

  if (!projectedDateKey) return false;

  return projectedDateKey < todayKey;
};

const filterItemsByTaskName = (items, searchText) => {
  const normalizedSearchText = normalizeSearchText(searchText);

  if (!normalizedSearchText) return [];

  return items.filter((item) => {
    const taskName = normalizeSearchText(item.preventiveTaskName);
    const taskCode = normalizeSearchText(item.companyPreventiveTaskId);

    return (
      taskName.includes(normalizedSearchText) ||
      taskCode.includes(normalizedSearchText)
    );
  });
};

const buildProjectionExecutionBody = (
  projectionItem,
  formValues,
  taskResources = [],
) => {
  const selectedStatus =
    getSelectValue(formValues.status) ||
    PREVENTIVE_TASK_EXECUTION_STATUS.DONE.value;

  const isResourceRow = projectionItem.rowType === 'RESOURCE';

  const hasTaskResources = taskResources.length > 0;

  const useResourceMode = hasTaskResources || isResourceRow;

  const selectedResourceIds = normalizeSelectedIds(
    formValues.completedResourceIds,
  );

  const attachedFile = normalizeFile(formValues.attachedFile);

  let resources = [];

  if (hasTaskResources) {
    resources = taskResources
      .filter((resource) => {
        const resourceId = getResourceId(resource);

        return selectedResourceIds.has(String(resourceId));
      })
      .map((resource) => {
        const resourceId = getResourceId(resource);

        return {
          resourceId,
          preventiveTaskProjectionResourceId: getProjectionResourceId(resource),

          startedDate:
            tzNormalizeDate({ date: formValues.startedDate }) || null,

          completedDate:
            tzNormalizeDate({ date: formValues.completedDate }) || null,

          status: selectedStatus,

          comment: formValues.comment || null,
        };
      });
  } else if (isResourceRow) {
    resources = [
      {
        resourceId: projectionItem.resourceId,
        preventiveTaskProjectionResourceId: projectionItem.projectionResourceId,

        startedDate: tzNormalizeDate({ date: formValues.startedDate }) || null,

        completedDate:
          tzNormalizeDate({ date: formValues.completedDate }) || null,

        status: selectedStatus,
        comment: formValues.comment || null,
      },
    ];
  }

  return {
    preventiveTaskId: projectionItem.preventiveTaskId,
    preventiveTaskProjectionId: projectionItem.projectionId,

    scheduleDate:
      tzNormalizeDate({
        date: projectionItem.projectedDate,
        time: 'start',
      }) || null,

    startedDate: tzNormalizeDate({ date: formValues.startedDate }) || null,
    completedDate: tzNormalizeDate({ date: formValues.completedDate }) || null,

    executionStatus: projectionItem.alertStatus || null,

    status: selectedStatus,

    resourceExecutionMode: useResourceMode
      ? RESOURCE_EXECUTION_MODE.RESOURCE_BASED.value
      : RESOURCE_EXECUTION_MODE.SAME_DATE.value,

    executorUserId: null,
    comment: formValues.comment || null,
    observation: formValues.observation || null,

    extensionDays: formValues.extensionDays
      ? Number(formValues.extensionDays)
      : null,

    postponePendingResources: false,

    attachedFile,
    spentAmount: formValues.spentAmount || null,

    resources,
  };
};

const PreventiveTaskProjectionCalendar = ({
  breadcrumbs,
  loading,
  loadingExecution,
  dashboardItems,
  dispatchGetPreventiveTaskProjectionDashboard,
  dispatchClearPreventiveTaskProjectionDashboard,
  dispatchPostPreventiveTaskExecution,
}) => {
  const today = new Date();

  const [visibleDate, setVisibleDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  const [selectedDateKey, setSelectedDateKey] = useState(toDateKey(today));
  const [daysToAdd, setDaysToAdd] = useState(0);
  const [view, setView] = useState('USER');

  const [detailMode, setDetailMode] = useState(DETAIL_MODE.DAY);
  const [taskSearchText, setTaskSearchText] = useState('');

  useLayoutEffect(() => {
    dispatchGetPreventiveTaskProjectionDashboard({
      daysToAdd: 0,
      view: 'USER',
    });

    return () => dispatchClearPreventiveTaskProjectionDashboard();
  }, []);

  const items = useMemo(() => dashboardItems.toJS(), [dashboardItems]);

  const itemsByDate = useMemo(() => groupByProjectedDate(items), [items]);

  const year = visibleDate.getFullYear();
  const month = visibleDate.getMonth();

  const weeks = useMemo(() => getMonthMatrix(year, month), [year, month]);

  const selectedDayItems = itemsByDate[selectedDateKey] || [];

  const todayKey = useMemo(() => toDateKey(new Date()), []);

  const overdueItems = useMemo(
    () => items.filter((item) => isOverdueProjection(item, todayKey)),
    [items, todayKey],
  );

  const searchedItems = useMemo(
    () => filterItemsByTaskName(items, taskSearchText),
    [items, taskSearchText],
  );

  const detailItems = useMemo(() => {
    if (detailMode === DETAIL_MODE.OVERDUE) return overdueItems;

    if (detailMode === DETAIL_MODE.SEARCH) return searchedItems;

    return selectedDayItems;
  }, [detailMode, overdueItems, searchedItems, selectedDayItems]);

  const detailTitle = useMemo(() => {
    if (detailMode === DETAIL_MODE.OVERDUE) {
      return `Tareas vencidas: ${overdueItems.length}`;
    }

    if (detailMode === DETAIL_MODE.SEARCH) {
      return taskSearchText
        ? `Resultado de búsqueda: "${taskSearchText}"`
        : 'Buscar tareas';
    }

    return `Detalle del día: ${formatDateTitle(selectedDateKey)}`;
  }, [detailMode, overdueItems.length, taskSearchText, selectedDateKey]);

  const detailEmptyMessage = useMemo(() => {
    if (detailMode === DETAIL_MODE.OVERDUE) {
      return 'No hay tareas vencidas.';
    }

    if (detailMode === DETAIL_MODE.SEARCH) {
      return taskSearchText
        ? 'No se encontraron tareas con ese nombre o código.'
        : 'Ingrese un nombre o código de tarea para buscar.';
    }

    return 'No hay proyecciones para este día.';
  }, [detailMode, taskSearchText]);

  const goPreviousMonth = () => {
    setVisibleDate(new Date(year, month - 1, 1));
  };

  const goNextMonth = () => {
    setVisibleDate(new Date(year, month + 1, 1));
  };

  const onSelectCalendarDate = (dateKey) => {
    setSelectedDateKey(dateKey);
    setDetailMode(DETAIL_MODE.DAY);
  };

  const onSubmitSearch = (formValues) => {
    const normalizedDaysToAdd = normalizeDaysToAdd(formValues.daysToAdd);
    const selectedView = formValues.view || 'USER';

    setDaysToAdd(normalizedDaysToAdd);
    setView(selectedView);

    dispatchGetPreventiveTaskProjectionDashboard({
      daysToAdd: normalizedDaysToAdd,
      view: selectedView,
    });
  };

  const onClickExecute = async (
    projectionItem,
    formValues,
    taskResources = [],
  ) => {
    const body = buildProjectionExecutionBody(
      projectionItem,
      formValues,
      taskResources,
    );

    const requiresResourceSelection =
      body.resourceExecutionMode ===
        RESOURCE_EXECUTION_MODE.RESOURCE_BASED.value &&
      taskResources.length > 0;

    if (requiresResourceSelection && !body.resources.length) {
      toastr.warning(
        'Seleccione al menos un recurso',
        'Debe marcar al menos un recurso para registrar la ejecución.',
      );

      return null;
    }

    const savedExecution = await dispatchPostPreventiveTaskExecution(body, {
      redirect: false,
    });

    dispatchGetPreventiveTaskProjectionDashboard({
      daysToAdd,
      view,
    });

    return savedExecution;
  };

  const content = loading ? (
    <Loader />
  ) : (
    <>
      <PreventiveTaskProjectionSearchForm
        onSubmit={onSubmitSearch}
        initialValues={{
          daysToAdd,
          view,
        }}
        loading={loading}
      />

      <PreventiveTaskProjectionHeader
        year={year}
        month={month}
        daysToAdd={daysToAdd}
        onPreviousMonth={goPreviousMonth}
        onNextMonth={goNextMonth}
      />

      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-center">
            <Col md="3">
              <Button
                className="w-100"
                color={
                  detailMode === DETAIL_MODE.OVERDUE
                    ? 'danger'
                    : 'outline-danger'
                }
                onClick={() => setDetailMode(DETAIL_MODE.OVERDUE)}
              >
                Ver vencidas ({overdueItems.length})
              </Button>
            </Col>

            <Col md="6">
              <Input
                value={taskSearchText}
                placeholder="Buscar tarea por nombre o código..."
                onChange={(event) => {
                  // eslint-disable-next-line prefer-destructuring
                  const value = event.target.value;

                  setTaskSearchText(value);

                  if (value.trim()) {
                    setDetailMode(DETAIL_MODE.SEARCH);
                  } else {
                    setDetailMode(DETAIL_MODE.DAY);
                  }
                }}
              />
            </Col>

            <Col md="3">
              <Button
                className="w-100"
                color="light"
                onClick={() => {
                  setTaskSearchText('');
                  setDetailMode(DETAIL_MODE.DAY);
                }}
              >
                Limpiar
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Row>
        <Col md="8">
          <PreventiveTaskProjectionMonthGrid
            weeks={weeks}
            itemsByDate={itemsByDate}
            selectedDateKey={selectedDateKey}
            onSelectDate={onSelectCalendarDate}
          />
        </Col>

        <Col md="4">
          <PreventiveTaskProjectionDayDetail
            selectedDateKey={selectedDateKey}
            selectedDayItems={detailItems}
            title={detailTitle}
            emptyMessage={detailEmptyMessage}
            showProjectedDate={detailMode !== DETAIL_MODE.DAY}
            onClickExecute={onClickExecute}
            loading={loadingExecution}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Proyección de Tareas Programadas"
      subtitle="Proyección mensual de tareas pendientes, por vencer y vencidas"
      content={content}
    />
  );
};

PreventiveTaskProjectionCalendar.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  loading: PropTypes.bool,
  loadingExecution: PropTypes.bool,
  dashboardItems: PropTypes.instanceOf(Immutable.List).isRequired,
  dispatchGetPreventiveTaskProjectionDashboard: PropTypes.func.isRequired,
  dispatchClearPreventiveTaskProjectionDashboard: PropTypes.func.isRequired,
  dispatchPostPreventiveTaskExecution: PropTypes.func.isRequired,
};

PreventiveTaskProjectionCalendar.defaultProps = {
  loading: false,
  loadingExecution: false,
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  breadcrumbs: [
    ...TaskScheduleUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Calendario Proyección de Tareas Programadas',
      href: '',
    },
  ],
  dashboardItems: TaskScheduleUnit.PreventiveTaskProjection.getIn([
    'all',
    'content',
  ]),
  loading: TaskScheduleUnit.PreventiveTaskProjection.getIn(['all', 'loading']),
  loadingExecution: !TaskScheduleUnit.PreventiveTaskExecution.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

const mapDispatchToProps = {
  dispatchGetPreventiveTaskProjectionDashboard:
    getPreventiveTaskProjectionDashboard,
  dispatchClearPreventiveTaskProjectionDashboard:
    clearPreventiveTaskProjectionDashboard,
  dispatchPostPreventiveTaskExecution: postPreventiveTaskExecution,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PreventiveTaskProjectionCalendar);
